import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import {
  SinglePanelBusModule as BusModule,
  PanelHeaderDivider,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { ReserveVehiclePanelActions } from "features/CenterContent/VehicleDetails/ReserveAlternativeTransportDetails/ReserveVehiclePanelActions";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import {
  VehicleSummary,
  VehicleTitle,
} from "features/CenterContent/VehicleDetails/shared/VehicleSummary";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { Module } from "shared/components/Module";
import { Text, VStack } from "shared/components/Spor";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import * as z from "zod";

const ReserveAlternativeTransportDetails = () => {
  const { vehicleUuid } = useParams();

  const { status, data: reserveVehicle } = useQuery({
    queryKey: ["reserveAltTransport", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/reserve/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  const sectionState = useMemo(
    () => ({
      fetchStatus: status,
      isEmpty: !reserveVehicle,
      emptyReason: "Fant ingen detaljer om bussen",
    }),
    [status, reserveVehicle],
  );

  return (
    <>
      <TabsCenter isReserveBus />
      <BusModule
        sectionState={sectionState}
        title={
          reserveVehicle && (
            <VehicleTitle
              {...reserveVehicle}
              title={`Reserve ${reserveVehicle.workShiftId}`}
            />
          )
        }
        action1={
          reserveVehicle && (
            <ReserveVehiclePanelActions vehicle={reserveVehicle} />
          )
        }
      >
        {reserveVehicle && (
          <>
            <PanelHeaderDivider>
              <VehicleSummary
                variant="reserve-backup"
                {...reserveVehicle}
                availableFrom={reserveVehicle.shiftStartTime}
                availableTo={reserveVehicle.shiftEndTime}
                location={reserveVehicle.baseStopName}
                reference={
                  reserveVehicle.isirkId ?? reserveVehicle.referenceNumber
                }
                baseStopReference={reserveVehicle.baseStopReference}
              />
            </PanelHeaderDivider>
            <PanelScrollContent>
              <PanelScrollArea>
                <PanelModuleGrid>
                  <VStack
                    style={{
                      width: "100%",
                      padding: 0,
                      alignItems: "stretch",
                    }}
                  >
                    {reserveVehicle.notes.length > 0 && (
                      <div style={{ width: "100%", padding: "0.75rem" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {reserveVehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    )}
                    <Module title="Logg">
                      <OperationalLog
                        vehicleId={reserveVehicle.id}
                        logs={reserveVehicle.logs ?? []}
                      />
                    </Module>
                  </VStack>
                </PanelModuleGrid>
              </PanelScrollArea>
            </PanelScrollContent>
          </>
        )}
      </BusModule>
    </>
  );
};

export default withErrorBoundary(ReserveAlternativeTransportDetails);
